import { z } from '../zod';
import { hasAtLeastOneField } from '../shared/refines';
import {
  FieldInputObjectValidationSchema,
  FieldShowHideLogicInputSchema,
  withFieldInputRefinements,
} from './create';
import { FieldParamsValidationSchema } from './params';

const fieldUpdateBodyShape = FieldInputObjectValidationSchema.omit({ key: true })
  .partial()
  .extend({
    showHideLogic: FieldShowHideLogicInputSchema.nullish().meta({
      description:
        'Conditional visibility rules for this field. `element.elementId` must be the ID of another field on the same page, and `value` an option ID on that field (or `TRUE`/`FALSE` for a checkbox). Read the IDs from the page detail response; create-time keys are rejected here.',
    }),
    options: FieldInputObjectValidationSchema.shape.options.meta({
      description:
        'Selectable options for `select`, `multi-select` and `multiple-choice` elements. Replaced wholesale: every option gets a new ID, so re-point any other field whose showHideLogic references one of these options afterwards. Options on a `multi-select` need different values (an option without a value uses its label), and options on a `multiple-choice` need different labels.',
    }),
  }).shape;

/**
 * Exported so the MCP tool can iterate the same property list instead of
 * re-declaring it (drift-proof).
 */
export const FIELD_UPDATE_FIELDS = Object.keys(fieldUpdateBodyShape) as ReadonlyArray<
  keyof typeof fieldUpdateBodyShape
>;

export const FieldUpdateBodyValidationSchema = withFieldInputRefinements(
  z.object(fieldUpdateBodyShape),
);

export const UpdateFieldInputValidationSchema = withFieldInputRefinements(
  z.object({
    ...FieldParamsValidationSchema.shape,
    ...fieldUpdateBodyShape,
  }),
).refine(
  value => {
    const { pageId: _pageId, pageType: _pageType, fieldId: _fieldId, ...rest } = value;
    return hasAtLeastOneField(rest);
  },
  { message: 'At least one field property to update must be provided.' },
);

export type UpdateFieldInput = z.infer<typeof UpdateFieldInputValidationSchema>;
